import React from 'react';
import styled from 'styled-components';
import ScrollAnimation from 'react-animate-on-scroll';
import { FormattedMessage, useIntl } from 'react-intl';
import { routesEnum } from '../../../Routes';
import { Heading } from '../../../pulsechain_components/Heading';
import { Text } from '../../../pulsechain_components/Text';
import { Link } from '../../../pulsechain_components/Link';
import { Button } from '../../../pulsechain_components/Button';
import { numberWithCommas } from '../../../utils/numberWithCommas';
import { TICKER_NAME } from '../../../utils/envVars';
import calculateStakingRewards from '../../../utils/calculateStakingRewards';

//
// Styled Components

const Container = styled.div`
  background-color: ${(p: { theme: any }) => p.theme.gray.dark};
  position: relative;
  padding: ${(p: { isMobile: boolean }) => (p.isMobile ? '64px 0' : '20px 0')};
`;
const Content = styled.div`
  max-width: ${p => p.theme.screenSizes.largest};
  width: 100%;
  margin: 0 auto;
  padding: 0 120px;
  @media only screen and (max-width: ${p => p.theme.screenSizes.largest}) {
    padding: ${(p: { isMobile: boolean }) =>
      p.isMobile ? '0 20px' : '0 60px'};
  }
`;

const BoldWhite = styled.span`
  color: white;
  font-size: ${(p: { theme: any; fontSize: number }) => p.fontSize}px;
  font-weight: bold;
`;

const Card = styled.div`
  padding: 20px;
  border: 1px solid #323234;
  border-radius: 12px;
  width: 100%;
  margin: 16px;
  background: #202022;
  @media only screen and (max-width: ${p => p.theme.screenSizes.medium}) {
    margin: 0px;
    margin-top: 16px;
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (max-width: ${p => p.theme.screenSizes.medium}) {
    flex-direction: column;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

type PropData = {
  amountEth: number;
  totalValidators: number;
  status: number;
};

//
// Main Component

export const NetworkStatus: React.FC<{
  state: PropData;
}> = ({ state }): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const [m, setM] = React.useState<boolean>((window as any).mobileCheck());
  const { amountEth, totalValidators, status } = state;

  React.useEffect(() => {
    const resizeListener = () => {
      setM((window as any).mobileCheck());
    };
    window.addEventListener('resize', resizeListener);
    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  const currentAPR = calculateStakingRewards({ totalAtStake: amountEth });
  const formattedAPR = (Math.round(currentAPR * 1000) / 10).toLocaleString();

  const LoadingHandler: React.FC<{
    value?: string;
  }> = ({ value }): JSX.Element => {
    if (status === 200) {
      return <span>{value}</span>;
    }
    if (status === 500) {
      return <FormattedMessage defaultMessage="Loading error" />;
    }
    return <FormattedMessage defaultMessage="Loading..." />;
  };

  return (
    <Container isMobile={m}>
      <ScrollAnimation animateIn="fadeIn" animateOnce>
        <Content isMobile={m}>
          <ButtonContainer className="mb20 pt40">
            <Link isTextLink={false} to={routesEnum.acknowledgementPage}>
              <Button
                fullWidth
                width={m ? undefined : 300}
                label={formatMessage({ defaultMessage: 'Become a validator' })}
              />
            </Link>
            <Link isTextLink={false} to={routesEnum.checklistPage}>
              <Button
                fullWidth
                width={m ? undefined : 300}
                label={formatMessage({ defaultMessage: 'Learn about staking' })}
              />
            </Link>
          </ButtonContainer>
          <CardContainer>
            <Card>
              <Heading level={3} size="medium" color="white" margin="none">
                <FormattedMessage
                  defaultMessage="Total {TICKER_NAME} staked"
                  values={{ TICKER_NAME }}
                />
              </Heading>
              <Text size="x-large" className="mt20">
                <BoldWhite className="mr10" fontSize={24}>
                  <LoadingHandler
                    value={`${numberWithCommas(amountEth)} ${TICKER_NAME}`}
                  />
                </BoldWhite>
              </Text>
            </Card>
            <Card>
              <Heading level={3} size="medium" color="white" margin="none">
                <FormattedMessage defaultMessage="Total validators" />
              </Heading>
              <Text size="x-large" className="mt20">
                <BoldWhite className="mr10" fontSize={24}>
                  <LoadingHandler value={numberWithCommas(totalValidators)} />
                </BoldWhite>
              </Text>
            </Card>
            <Card>
              <Heading level={3} size="medium" color="white" margin="none">
                <FormattedMessage
                  defaultMessage="Current APR"
                  description="APR refers to Annual Percentage Rate"
                />
              </Heading>
              <Text size="x-large" className="mt20">
                <BoldWhite className="mr10" fontSize={24}>
                  {formattedAPR}%
                </BoldWhite>
              </Text>
            </Card>
          </CardContainer>
        </Content>
      </ScrollAnimation>
    </Container>
  );
};
